import { formatCurrency } from '../../utils/formatting';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Break-even analysis card for new-hire-revenue scenarios.
 * Shows the month the hire becomes profitable, a progress bar of
 * loss vs profit months, and the cumulative P&L through December.
 */
export default function ScenarioBreakEven({ scenario }) {
  // Only render for revenue-generating hires
  if (scenario.type !== 'new-hire-revenue') return null;

  const monthlyCost = scenario.monthlySalary * (1 + scenario.payrollTaxRate);
  const monthlyRevenue = scenario.monthlyRevenue || 0;
  const hireStart = scenario.startMonth; // 1-indexed
  const revenueStart = scenario.revenueStartMonth || hireStart;

  // Build per-month net from hire start through December (months 1-12)
  let cumulative = 0;
  let breakEvenMonth = null; // 1-indexed month when cumulative first goes >= 0
  const monthData = [];

  for (let m = 1; m <= 12; m++) {
    let net = 0;
    if (m >= hireStart) net -= monthlyCost;
    if (m >= revenueStart) net += monthlyRevenue;
    cumulative += net;
    monthData.push({ month: m, net, cumulative });

    // Break-even is the first month where cumulative is >= 0 and we are past
    // at least one month of cost (meaning we had losses to recover from)
    if (breakEvenMonth === null && cumulative >= 0 && m >= hireStart) {
      breakEvenMonth = m;
    }
  }

  const finalCumulative = cumulative;
  const monthlyNet = monthlyRevenue - monthlyCost;

  // Count loss months (negative net) and profit months (positive net) for progress bar
  const activeMonths = monthData.filter(d => d.month >= hireStart);
  const lossMonths = activeMonths.filter(d => d.net < 0).length;
  const profitMonths = activeMonths.filter(d => d.net > 0).length;
  const neutralMonths = activeMonths.length - lossMonths - profitMonths;
  const totalActive = activeMonths.length || 1;

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4 border-l-4 border-l-[#2596BE] ml-4 mt-1">
      <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#006D78] mb-2">
        Break-Even Analysis
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <div className="text-[10px] text-[#888] uppercase tracking-[0.05em]">Monthly Cost</div>
          <div className="text-[13px] font-bold text-[#FF6E83]">{formatCurrency(-monthlyCost)}</div>
        </div>
        <div>
          <div className="text-[10px] text-[#888] uppercase tracking-[0.05em]">Monthly Revenue</div>
          <div className="text-[13px] font-bold text-[#006D78]">{formatCurrency(monthlyRevenue)}</div>
        </div>
        <div>
          <div className="text-[10px] text-[#888] uppercase tracking-[0.05em]">Monthly Net</div>
          <div className={`text-[13px] font-bold ${monthlyNet >= 0 ? 'text-[#006D78]' : 'text-[#FF6E83]'}`}>
            {formatCurrency(monthlyNet)}
          </div>
        </div>
      </div>

      {/* Break-even status */}
      <div className="mb-3">
        {monthlyNet <= 0 ? (
          <div className="text-[12px] text-[#FF6E83] font-bold">
            Revenue does not cover costs. No break-even possible.
          </div>
        ) : breakEvenMonth !== null ? (
          <div className="text-[12px] text-[#006D78] font-bold">
            Break-even in {MONTH_NAMES[breakEvenMonth - 1]}
            {revenueStart > hireStart && (
              <span className="text-[#888] font-normal ml-1">
                ({revenueStart - hireStart} month{revenueStart - hireStart > 1 ? 's' : ''} ramp-up)
              </span>
            )}
          </div>
        ) : (
          <div className="text-[12px] text-[#FF6E83] font-bold">
            Does not break even by December
          </div>
        )}
      </div>

      {/* Progress bar: loss months (red) vs profit months (green) */}
      <div className="mb-2">
        <div className="flex h-3 rounded-[3px] overflow-hidden bg-gray-100">
          {lossMonths > 0 && (
            <div
              className="bg-[#FF6E83]"
              style={{ width: `${(lossMonths / totalActive) * 100}%` }}
              title={`${lossMonths} loss month${lossMonths > 1 ? 's' : ''}`}
            />
          )}
          {neutralMonths > 0 && (
            <div
              className="bg-gray-300"
              style={{ width: `${(neutralMonths / totalActive) * 100}%` }}
              title={`${neutralMonths} neutral month${neutralMonths > 1 ? 's' : ''}`}
            />
          )}
          {profitMonths > 0 && (
            <div
              className="bg-[#006D78]"
              style={{ width: `${(profitMonths / totalActive) * 100}%` }}
              title={`${profitMonths} profit month${profitMonths > 1 ? 's' : ''}`}
            />
          )}
        </div>
        <div className="flex justify-between text-[9px] text-[#888] mt-0.5">
          <span>{lossMonths} loss</span>
          <span>{profitMonths} profit</span>
        </div>
      </div>

      {/* Cumulative P&L by December */}
      <div className="pt-2 border-t border-gray-100">
        <span className="text-[11px] text-[#888]">By December: </span>
        <span className={`text-[13px] font-bold ${finalCumulative >= 0 ? 'text-[#006D78]' : 'text-[#FF6E83]'}`}>
          net {formatCurrency(finalCumulative)}
        </span>
      </div>
    </div>
  );
}
